import React, { useState, useEffect, FC } from "react";
import { useTranslation } from "react-i18next";

// MUI
import { Box, Typography, Divider, Fade, Chip } from "@mui/material";

// OL
import { transform } from "ol/proj";

// CUSTOM
import { IInfoPane } from "@/@types/views/GIS/map";
import WeatherCard from "@/views/GIS/MapPage/WeatherCard";
import { prepareCoordinatesForDisplay } from "@/lib/olHelpers";
import GeoURI from "@/ui/PublicPage/SidebarPanes/GeoURI";

import {
  InfoPaneShareActionButton,
  InfoPaneAddActionButton,
  InfoPaneCommentActionButton,
  InfoPaneWeatherActionButton,
  InfoPaneActionButtons
}
  from "@/ui/PublicPage/PublicActionButtons/";

type RandomClickInfoProps = {
  infoPane?: IInfoPane;
  handleAdd: (coord: number[] | undefined, label: string) => void;
  handleShowCommentPane: (newCommentPane: object) => void;
  handleOpenLDTPane?: (ldtId: number, ldtStageId: number) => void;
};


const RandomClickInfo: FC<RandomClickInfoProps> = (props) => {
  const { infoPane, handleShowCommentPane, handleAdd, handleOpenLDTPane } = props;
  const { t } = useTranslation();

  const [fetchWeatherData, setFetchWeatherData] = useState(false);

  const coordinates = infoPane?.data.coordinates as number[]; // EPSG:3857

  const coordinatesTransformed = transform(coordinates, "EPSG:3857", "EPSG:4326");

  const displayCoordinatesDecimal = [coordinatesTransformed[0].toFixed(7), coordinatesTransformed[1].toFixed(7)]

  const displayCoordinates = prepareCoordinatesForDisplay(coordinates);

  const handleAddToRoute = (coord: number[] | undefined) => {
    handleAdd(
      coord,
      `${displayCoordinates[1]}N ${displayCoordinates[0]}E`
    );
  };

  const handleFetchWeatherData = () => {
    setFetchWeatherData(true);
  }

  useEffect(() => {
    setFetchWeatherData(false);
  }, [infoPane])

  const dmsCoordinates = `${displayCoordinates[1]}N ${displayCoordinates[0]}E`;
  const degCoordinates = `${displayCoordinatesDecimal[1]}°N ${displayCoordinatesDecimal[0]}°E`

  return (
    <Box height="100%" width="100%" p={2}>
      <Box display="flex" flexDirection="column">
        <Box mb={2}>
          <GeoURI coords4326={coordinatesTransformed} display={dmsCoordinates} variant="body1" />
          <Typography variant="body1">
            {t("titles.degminsec")}
          </Typography>
        </Box>
        <Box mb={2}>
          <GeoURI coords4326={coordinatesTransformed} display={degCoordinates} variant="body1" />
          <Typography variant="body1">
            {t("titles.decimal")}
          </Typography>
        </Box>
      </Box>
      <Box>
        {infoPane?.data.chips && infoPane?.data.chips?.length > 0 ? infoPane?.data.chips?.map((ldtChip) => <Chip 
            label={ldtChip.displayName} 
            color="primary" 
            onClick={() => {
              handleOpenLDTPane ? handleOpenLDTPane(ldtChip.id, ldtChip.initialIdToSelect as number) : () => {}
            }} 
            style={{
              marginTop: 5,
              marginLeft: 2,
              color: '#DDFFD7'
            }} />
        ) : null}
        <InfoPaneActionButtons>
          <InfoPaneAddActionButton
            label={infoPane?.data.record?.naziv as string}
            initialCoordinates={coordinates} onAdd={handleAddToRoute}
          />
          <InfoPaneShareActionButton
            infoPane={infoPane}
          />
          <InfoPaneCommentActionButton
            handleGoToComments={handleShowCommentPane}
            // @ts-ignore
            data={infoPane?.data}
          />
          <InfoPaneWeatherActionButton
            handleFetchWeatherData={handleFetchWeatherData}
          />
        </InfoPaneActionButtons>
      </Box>
      <Box mt={5} />
      <Fade in={fetchWeatherData}>
        <Box>
          <Divider variant="middle" sx={{ marginY: 3, borderColor: "#000000" }} />
          <WeatherCard
            lat={coordinatesTransformed[1]}
            lon={coordinatesTransformed[0]}
            fetchData={fetchWeatherData}
          />
        </Box>
      </Fade>
    </Box>
  );
}

export default RandomClickInfo;
